"use client";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const COLS = 20;
const ROWS = 12;
const TOTAL = COLS * ROWS;

const colors = [
  "hsl(210, 100%, 19%)", // brand navy #003060
  "hsl(183, 100%, 35%)", // brand teal #00A8B0
  "hsl(183, 80%, 42%)", // lighter teal
  "hsl(210, 70%, 32%)", // mid navy
  "hsl(210, 100%, 12%)", // deep navy
];

// Deterministic set of cells to highlight, rotated over time
function pickCells(step: number, count: number): number[] {
  const cells: number[] = [];
  // Use a simple hash-like approach for variety
  for (let i = 0; i < count; i++) {
    cells.push(((step * 37 + i * 53 + 17) * 97) % TOTAL);
  }
  // Remove duplicates
  return [...new Set(cells)];
}

const AnimatedGrid = () => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setStep((s) => s + 1);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  const activeCells = pickCells(step, 6);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Static grid lines */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-40" />

      {/* Animated colored squares */}
      <AnimatePresence mode="popLayout">
        {activeCells.map((cellIndex) => {
          const col = cellIndex % COLS;
          const row = Math.floor(cellIndex / COLS);
          const colorIndex = (cellIndex + step) % colors.length;

          return (
            <motion.div
              key={`${step}-${cellIndex}`}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 0.3, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{
                duration: 1.8,
                ease: "easeInOut",
                delay: Math.random() * 0.4,
              }}
              className="absolute rounded-sm"
              style={{
                left: `${col * 4}rem`,
                top: `${row * 4}rem`,
                width: "4rem",
                height: "4rem",
                backgroundColor: colors[colorIndex],
                opacity: 0.06,
              }}
            />
          );
        })}
      </AnimatePresence>
    </div>
  );
};

export default AnimatedGrid;
